import { useState } from 'react';
import { Button, Field, Notice, Spinner } from '../../components/ui';
import { Dialog } from '../../components/Dialog';
import { ApiError } from '../../lib/api';
import { useAdminModels, useSaveModel, type AdminReflectionModel } from '../../lib/development';
import '../../components/Form.css';
import '../Page.css';
import '../Hub.css';
import '../Development.css';

type Draft = {
  id?: string;
  name: string;
  summary: string;
  source: string;
  forLessons: boolean;
  active: boolean;
  sortOrder: number;
  prompts: { key: string; question: string; hint: string }[];
};

const blank: Draft = { name: '', summary: '', source: 'EducAitor', forLessons: false, active: true, sortOrder: 50, prompts: [{ key: 'q1', question: '', hint: '' }] };

/**
 * Reflection models are content: EducAitor admins add and edit them here,
 * including partner programmes (set the source to the partner's name). Leaders
 * choose from the active ones for a target; "for lessons" models are the quick
 * reflection after a lesson.
 */
export function AdminReflectionModelsPage() {
  const models = useAdminModels();
  const [editing, setEditing] = useState<Draft | null>(null);

  const edit = (m: AdminReflectionModel) =>
    setEditing({
      id: m.id,
      name: m.name,
      summary: m.summary ?? '',
      source: m.source,
      forLessons: m.forLessons,
      active: m.active,
      sortOrder: m.sortOrder,
      prompts: m.prompts.map((p) => ({ key: p.key, question: p.question, hint: p.hint ?? '' })),
    });

  return (
    <div className="page">
      <header className="page-head dev-head">
        <div>
          <h1>Reflection models</h1>
          <p className="muted">
            The questions teachers answer when they reflect. Leaders choose one per development target. Add a partner&rsquo;s programme by setting its source
            to their name. Changes apply to new reflections; ones already written keep their answers.
          </p>
        </div>
        <Button onClick={() => setEditing({ ...blank, prompts: [...blank.prompts] })}>New model</Button>
      </header>

      {models.isLoading ? (
        <Spinner />
      ) : models.isError ? (
        <Notice tone="error">We couldn&rsquo;t load the models.</Notice>
      ) : (
        <div className="dev-targets">
          {(models.data ?? []).map((m) => (
            <article key={m.id} className="panel dev-target">
              <div className="dev-target-top">
                <div className="dev-target-title">
                  <h2>{m.name}</h2>
                  <p className="faint">
                    {m.source}
                    {m.forLessons ? ' · after a lesson' : ' · for targets'}
                    {m.active ? '' : ' · hidden'}
                  </p>
                </div>
                <Button variant="secondary" onClick={() => edit(m)}>
                  Edit
                </Button>
              </div>
              {m.summary ? <p className="dev-why">{m.summary}</p> : null}
              <ol className="dev-prompts">
                {m.prompts.map((p) => (
                  <li key={p.key}>{p.question}</li>
                ))}
              </ol>
            </article>
          ))}
        </div>
      )}

      {editing ? <ModelEditor draft={editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

function ModelEditor({ draft, onClose }: { draft: Draft; onClose: () => void }) {
  const save = useSaveModel();
  const [d, setD] = useState<Draft>(draft);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));
  const setPrompt = (i: number, patch: Partial<Draft['prompts'][number]>) =>
    setD((x) => ({ ...x, prompts: x.prompts.map((p, j) => (j === i ? { ...p, ...patch } : p)) }));

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({
        id: d.id,
        name: d.name,
        summary: d.summary,
        source: d.source,
        forLessons: d.forLessons,
        active: d.active,
        sortOrder: d.sortOrder,
        prompts: d.prompts.map((p) => ({ key: p.key, question: p.question, hint: p.hint || null })),
      });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That didn’t save. Try again.');
    }
  };

  return (
    <Dialog open onClose={onClose} title={d.id ? `Edit ${draft.name}` : 'New reflection model'} wide>
      <div className="dev-editor">
        <Field id="rm-name" label="Name" value={d.name} onChange={(e) => set('name', e.target.value)} maxLength={120} placeholder="e.g. After Action Review" />
        <Field id="rm-summary" label="One-line summary" value={d.summary} onChange={(e) => set('summary', e.target.value)} maxLength={255} />
        <Field id="rm-source" label="Source" value={d.source} onChange={(e) => set('source', e.target.value)} maxLength={80} hint="EducAitor, or the partner who wrote it" />
        <label className="auth-check">
          <input type="checkbox" checked={d.forLessons} onChange={(e) => set('forLessons', e.target.checked)} />
          <span>Use after a single lesson (short), rather than at a target&rsquo;s check-ins</span>
        </label>
        <label className="auth-check">
          <input type="checkbox" checked={d.active} onChange={(e) => set('active', e.target.checked)} />
          <span>Available to choose</span>
        </label>

        <fieldset className="choice">
          <legend>Questions, in order</legend>
          <div className="dev-cp-rows">
            {d.prompts.map((p, i) => (
              <div key={i} className="dev-model-q">
                <Field id={`rm-q-${i}`} label={`Question ${i + 1}`} value={p.question} onChange={(e) => setPrompt(i, { question: e.target.value })} maxLength={200} />
                <Field id={`rm-h-${i}`} label="Hint (optional)" value={p.hint} onChange={(e) => setPrompt(i, { hint: e.target.value })} maxLength={240} />
                <div className="dev-model-q-foot">
                  <span className="faint">Key: {p.key}</span>
                  {d.prompts.length > 1 ? (
                    <button type="button" className="hub-link" onClick={() => setD((x) => ({ ...x, prompts: x.prompts.filter((_, j) => j !== i) }))}>
                      Remove
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
          {d.prompts.length < 8 ? (
            <button
              type="button"
              className="hub-link"
              onClick={() =>
                setD((x) => {
                  let n = x.prompts.length + 1;
                  while (x.prompts.some((p) => p.key === `q${n}`)) n += 1;
                  return { ...x, prompts: [...x.prompts, { key: `q${n}`, question: '', hint: '' }] };
                })
              }
            >
              Add a question
            </button>
          ) : null}
        </fieldset>

        {error ? <Notice tone="error">{error}</Notice> : null}
        <div className="dialog-actions">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} loading={save.isPending}>
            {d.id ? 'Save changes' : 'Add model'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
